import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import useAutoRefresh from '../../hooks/useAutoRefresh';
import AdminLayout from '../../components/layout/AdminLayout';
import SupervisorLayout from '../../components/layout/SupervisorLayout';
import PayrollLayout from '../../components/layout/PayrollLayout';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Banknote, Upload, Eye, Download, Trash2, Search, Loader2, CheckCircle2, ChevronDown, ChevronRight, Copy, ExternalLink, ShieldCheck, XCircle, UserPlus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DeptBadge from '../../components/DeptBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function lastMonths(n = 12) {
  const out = [];
  const d = new Date();
  d.setDate(1);
  for (let i = 0; i < n; i++) {
    d.setMonth(d.getMonth() - 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    out.push({ value: val, label: d.toLocaleString('en-GB', { month: 'long', year: 'numeric' }) });
  }
  return out;
}

export default function PayrollPage({ payrollMode = false }) {
  const { user } = useAuth();
  const Layout = payrollMode ? PayrollLayout : (user?.role === 'supervisor' ? SupervisorLayout : AdminLayout);
  const isMainAdmin = user?.role === 'admin';
  const months = lastMonths();
  const [month, setMonth] = useState(months[0].value);
  const [data, setData] = useState({ staff: [], month_label: '' });
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const fileRef = useRef(null);
  const uploadTarget = useRef(null);
  const [dragOverId, setDragOverId] = useState(null);
  const navigate = useNavigate();
  const [expandedId, setExpandedId] = useState(null);
  const [bankMap, setBankMap] = useState({});
  const [bankLoading, setBankLoading] = useState(false);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [approvalBusyId, setApprovalBusyId] = useState(null);
  // "Add employee" picker — manually pin ANY employee onto the list so a
  // payslip can be uploaded even if they have no shift this month.
  const [allStaff, setAllStaff] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addQuery, setAddQuery] = useState('');
  const addRef = useRef(null);
  useEffect(() => {
    if (!addOpen) return;
    const close = (e) => { if (addRef.current && !addRef.current.contains(e.target)) setAddOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('touchstart', close);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('touchstart', close); };
  }, [addOpen]);

  const fetchPendingApprovals = useCallback(async () => {
    if (!isMainAdmin) return;
    try {
      const res = await axios.get(`${API}/admin/payroll/payslips/pending-approval`);
      setPendingApprovals(res.data.payslips || []);
    } catch {}
  }, [isMainAdmin]);

  const approvePayslip = async (p) => {
    if (!window.confirm(`Approve ${p.employee_name}'s payslip for ${p.month_label}? It becomes visible in their My Area and they get an email.`)) return;
    setApprovalBusyId(p.id);
    try {
      const res = await axios.post(`${API}/admin/payroll/payslips/${p.id}/approve`);
      toast.success(res.data.message);
      fetchPendingApprovals();
      fetchStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Approval failed');
    } finally {
      setApprovalBusyId(null);
    }
  };

  const rejectPayslip = async (p) => {
    const reason = window.prompt(`Reject ${p.employee_name}'s payslip for ${p.month_label}?\n\nEnter the rejection reason (visible to the Payroll department):`);
    if (reason === null) return;
    if (!reason.trim()) { toast.error('A rejection reason is required'); return; }
    setApprovalBusyId(p.id);
    try {
      const res = await axios.post(`${API}/admin/payroll/payslips/${p.id}/reject`, { reason: reason.trim() });
      toast.success(res.data.message);
      fetchPendingApprovals();
      fetchStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rejection failed');
    } finally {
      setApprovalBusyId(null);
    }
  };

  const toggleBank = async (emp) => {
    if (!isMainAdmin) return;
    if (expandedId === emp.employee_id) { setExpandedId(null); return; }
    setExpandedId(emp.employee_id);
    if (!bankMap[emp.employee_id]) {
      setBankLoading(true);
      try {
        const res = await axios.get(`${API}/admin/employees/${emp.employee_id}/bank`);
        setBankMap((m) => ({ ...m, [emp.employee_id]: res.data }));
      } catch {
        toast.error('Could not load bank details');
      } finally {
        setBankLoading(false);
      }
    }
  };

  const copyVal = (label, val) => {
    navigator.clipboard?.writeText(val).then(() => toast.success(`${label} copied`));
  };

  const fetchStaff = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/payroll/staff`, { params: { month, show_all: showAll } });
      setData(res.data);
      setAllStaff(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load payroll list');
    } finally {
      setLoading(false);
    }
  }, [month, showAll]);

  useEffect(() => { fetchStaff(); }, [fetchStaff]);
  useEffect(() => {
    if (!addOpen || allStaff !== null) return;
    axios.get(`${API}/admin/payroll/staff`, { params: { month, show_all: true } })
      .then((r) => setAllStaff(r.data?.staff || []))
      .catch(() => setAllStaff([]));
  }, [addOpen, allStaff, month]);

  const addManual = async (s) => {
    setAddOpen(false);
    setAddQuery('');
    try {
      await axios.post(`${API}/admin/payroll/manual`, { employee_id: s.employee_id, month });
      toast.success(`${s.name} added to ${data.month_label || 'this month'}'s list`);
      fetchStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not add employee');
    }
  };

  const removeManual = async (s) => {
    if (!window.confirm(`Remove ${s.name} from this month's list?`)) return;
    try {
      await axios.delete(`${API}/admin/payroll/manual`, { params: { employee_id: s.employee_id, month } });
      fetchStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove');
    }
  };
  useEffect(() => { fetchPendingApprovals(); }, [fetchPendingApprovals]);
  useAutoRefresh(() => { fetchStaff(); fetchPendingApprovals(); }, { match: ['/payroll', '/payslip'] });

  const pickFile = (emp) => {
    uploadTarget.current = emp;
    fileRef.current?.click();
  };

  const uploadFor = async (emp, file) => {
    if (!file || !emp) return;
    if (file.type !== 'application/pdf' && !file.name?.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are accepted for payslips');
      return;
    }
    if (emp.payslip && !window.confirm(payrollMode
      ? `${emp.name} already has a payslip for ${data.month_label}. Replace it? The new file goes to the Primary Admin for approval.`
      : `${emp.name} already has a payslip for ${data.month_label}. Replace it? It will go back to HIDDEN until you activate it again.`)) return;
    const form = new FormData();
    form.append('employee_id', emp.employee_id);
    form.append('month', month);
    form.append('file', file);
    setBusyId(emp.employee_id);
    try {
      const res = await axios.post(`${API}/admin/payroll/payslips`, form);
      toast.success(res.data.message);
      fetchStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setBusyId(null);
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    await uploadFor(uploadTarget.current, file);
  };

  const toggleActive = async (emp) => {
    const p = emp.payslip;
    if (!p) return;
    setBusyId(emp.employee_id);
    try {
      const res = await axios.post(`${API}/admin/payroll/payslips/${p.id}/activate`, { active: !p.active });
      if (res.data.active) toast.success(`Payslip visible to ${emp.name}${res.data.email_sent ? ' — email sent' : ''}`);
      else toast.success(`Payslip hidden from ${emp.name}`);
      fetchStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setBusyId(null);
    }
  };

  const togglePaid = async (emp) => {
    setBusyId(emp.employee_id);
    try {
      const res = await axios.post(`${API}/admin/payroll/paid`, {
        employee_id: emp.employee_id, month, paid: !emp.paid,
      });
      toast.success(res.data.paid ? `${emp.name} marked as PAID` : `${emp.name} marked as NOT PAID`);
      fetchStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update payment status');
    } finally {
      setBusyId(null);
    }
  };

  const activateMonth = async () => {
    if (!window.confirm(`Activate ALL uploaded payslips for ${data.month_label}? Each employee will receive one notification email.`)) return;
    setBulkBusy(true);
    try {
      const res = await axios.post(`${API}/admin/payroll/activate-month`, { month });
      toast.success(res.data.message);
      fetchStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk activation failed');
    } finally {
      setBulkBusy(false);
    }
  };

  const removePayslip = async (emp) => {
    if (!window.confirm(`Delete ${emp.name}'s payslip for ${data.month_label}?`)) return;
    try {
      await axios.delete(`${API}/admin/payroll/payslips/${emp.payslip.id}`);
      toast.success('Payslip deleted');
      fetchStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const openPayslip = async (p) => {
    try {
      const res = await axios.get(`${API}/admin/payroll/payslips/${p.id}/download`, { responseType: 'blob' });
      window.open(window.URL.createObjectURL(res.data), '_blank');
    } catch {
      toast.error('Could not open payslip');
    }
  };

  const q = search.trim().toLowerCase();
  const staff = data.staff.filter((s) => !q || (s.name || '').toLowerCase().includes(q));
  const pendingCount = staff.filter((s) => s.payslip && !s.payslip.active
    && !['pending', 'rejected'].includes(s.payslip.approval_status)).length;

  return (
    <Layout>
      <div className="space-y-6" data-testid="payroll-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight uppercase flex items-center gap-2">
              <Banknote className="w-6 h-6 text-primary" /> Payroll
            </h1>
            <p className="text-sm text-muted-foreground">
              {payrollMode
                ? 'Upload monthly payslips. Every upload goes to the Primary Admin for approval before the employee can see it.'
                : 'Staff who worked in the selected month. Payslips stay hidden until you activate them.'}
            </p>
          </div>
          {isMainAdmin && pendingCount > 0 && (
            <Button onClick={activateMonth} disabled={bulkBusy}
              className="rounded-sm text-xs uppercase bg-primary text-primary-foreground hover:bg-yellow-300"
              data-testid="payroll-activate-month-btn">
              {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
              Activate all — {data.month_label} ({pendingCount})
            </Button>
          )}
        </div>

        {isMainAdmin && pendingApprovals.length > 0 && (
          <Card className="border-emerald-500/40 bg-emerald-500/5" data-testid="payslip-approvals-card">
            <CardContent className="p-4 space-y-3">
              <p className="text-sm font-barlow uppercase tracking-wider text-emerald-400 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4" /> Payslips pending approval — uploaded by Payroll ({pendingApprovals.length})
              </p>
              <div className="divide-y divide-border/50">
                {pendingApprovals.map((p) => (
                  <div key={p.id} className="flex flex-wrap items-center gap-2 py-2" data-testid={`approval-row-${p.id}`}>
                    <div className="min-w-0 flex-1 basis-full sm:basis-auto">
                      <p className="text-sm font-medium truncate">{p.employee_name}</p>
                      <p className="text-[11px] text-muted-foreground">
                        {p.month_label} · uploaded by {p.uploaded_by_name}
                        {p.uploaded_at ? ` on ${new Date(p.uploaded_at).toLocaleDateString('en-GB')}` : ''}
                      </p>
                    </div>
                    <Badge className="rounded-sm text-[10px] uppercase bg-emerald-500/15 text-emerald-400 border-emerald-500/40">
                      Payroll
                    </Badge>
                    <Button size="sm" variant="ghost" onClick={() => openPayslip(p)}
                      className="h-7 px-2 text-[11px]" data-testid={`approval-view-${p.id}`}>
                      <Eye className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" onClick={() => approvePayslip(p)} disabled={approvalBusyId === p.id}
                      className="h-7 px-3 rounded-sm text-[11px] uppercase bg-green-600 hover:bg-green-500 text-white"
                      data-testid={`approval-approve-${p.id}`}>
                      {approvalBusyId === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve</>}
                    </Button>
                    <Button size="sm" onClick={() => rejectPayslip(p)} disabled={approvalBusyId === p.id}
                      className="h-7 px-3 rounded-sm text-[11px] uppercase bg-red-600 hover:bg-red-500 text-white"
                      data-testid={`approval-reject-${p.id}`}>
                      <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <select value={month} onChange={(e) => setMonth(e.target.value)}
            className="h-9 bg-background border border-border rounded-sm px-2 text-sm"
            data-testid="payroll-month-select">
            {months.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search staff…"
              className="pl-9 h-9 w-56 rounded-sm bg-background border-border text-sm" data-testid="payroll-search-input" />
          </div>
          <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
            <Switch checked={showAll} onCheckedChange={setShowAll} data-testid="payroll-show-all-switch" />
            Show all staff
          </label>
          <div className="relative" ref={addRef}>
            <Button variant="outline" size="sm" onClick={() => { setAddOpen((v) => !v); setAddQuery(''); }}
              className="h-9 rounded-sm text-xs uppercase border-primary/30 text-primary hover:bg-primary/10"
              data-testid="payroll-add-employee-btn">
              <UserPlus className="w-3.5 h-3.5 mr-1.5" /> Add employee
            </Button>
            {addOpen && (
              <div className="absolute z-30 mt-1 left-0 w-[280px] bg-popover border border-border rounded-sm shadow-lg overflow-hidden" data-testid="payroll-add-employee-panel">
                <div className="p-2 border-b border-border">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                    Add an employee to this month's list
                  </p>
                  <Input value={addQuery} onChange={(e) => setAddQuery(e.target.value)} placeholder="Type a name…"
                    autoFocus className="h-7 text-xs rounded-sm" data-testid="payroll-add-employee-search" />
                </div>
                <div className="max-h-56 overflow-y-auto">
                  {allStaff === null && <p className="text-xs text-muted-foreground p-2">Loading…</p>}
                  {allStaff !== null && (() => {
                    const aq = addQuery.trim().toLowerCase();
                    const inList = new Set(data.staff.map((s) => s.employee_id));
                    const opts = allStaff
                      .filter((s) => !inList.has(s.employee_id))
                      .filter((s) => !aq || (s.name || '').toLowerCase().includes(aq))
                      .slice(0, 8);
                    if (opts.length === 0) {
                      return <p className="text-xs text-muted-foreground italic p-2">No matching employee (those already listed are hidden)</p>;
                    }
                    return opts.map((s) => (
                      <button key={s.employee_id} type="button"
                        onClick={() => addManual(s)}
                        className="w-full text-left px-2.5 py-1.5 text-xs hover:bg-primary/10"
                        data-testid={`payroll-add-option-${s.employee_id}`}>
                        {s.name}
                      </button>
                    ));
                  })()}
                </div>
              </div>
            )}
          </div>
        </div>

        <input type="file" ref={fileRef} accept="application/pdf,image/jpeg,image/png"
          className="hidden" onChange={onFile} data-testid="payroll-file-input" />

        {loading ? (
          <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>
        ) : staff.length === 0 ? (
          <Card className="border-border"><CardContent className="py-10 text-center text-sm text-muted-foreground" data-testid="payroll-empty">
            {q ? `No staff matching "${search}".` : `Nobody worked in ${data.month_label}. Use "Show all staff" if you need to attach a payslip anyway.`}
          </CardContent></Card>
        ) : (
          <Card className="border-border">
            <CardContent className="p-0 divide-y divide-border/50">
              {staff.map((s) => (
                <div
                  key={s.employee_id}
                  data-testid={`payroll-row-${s.employee_id}`}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); if (dragOverId !== s.employee_id) setDragOverId(s.employee_id); }}
                  onDragLeave={(e) => { e.preventDefault(); if (dragOverId === s.employee_id) setDragOverId(null); }}
                  onDrop={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragOverId(null);
                    const file = e.dataTransfer?.files?.[0];
                    await uploadFor(s, file);
                  }}
                  className={dragOverId === s.employee_id ? 'bg-primary/10 ring-1 ring-inset ring-primary/60' : ''}
                >
                  <div className="flex flex-wrap items-center gap-2 px-4 py-3">
                  <div
                    className={`min-w-0 flex-1 ${isMainAdmin ? 'cursor-pointer select-none' : ''}`}
                    onClick={() => toggleBank(s)}
                    data-testid={`payroll-name-${s.employee_id}`}
                  >
                    <p className="text-sm font-medium flex items-center gap-1">
                      {isMainAdmin && (expandedId === s.employee_id
                        ? <ChevronDown className="w-3.5 h-3.5 text-primary" />
                        : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />)}
                      {s.name}
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      {s.shifts} shift{s.shifts === 1 ? '' : 's'} · {s.hours}h in {data.month_label}
                      {s.manual && (
                        <span className="inline-flex items-center gap-1 ml-2 text-primary/80">
                          · added manually
                          <button type="button"
                            onClick={(e) => { e.stopPropagation(); removeManual(s); }}
                            className="text-muted-foreground hover:text-red-500"
                            title="Remove from this month's list"
                            data-testid={`payroll-remove-manual-${s.employee_id}`}>
                            <XCircle className="w-3.5 h-3.5" />
                          </button>
                        </span>
                      )}
                    </p>
                  </div>
                  {isMainAdmin ? (
                    <Button size="sm" onClick={() => togglePaid(s)} disabled={busyId === s.employee_id}
                      className={`h-7 px-3 rounded-sm text-[11px] font-bold uppercase tracking-wider text-white ${s.paid
                        ? 'bg-green-600 hover:bg-green-500'
                        : 'bg-red-600 hover:bg-red-500'}`}
                      title={s.paid && s.paid_at ? `Paid on ${new Date(s.paid_at).toLocaleDateString('en-GB')}` : 'Click to mark as paid'}
                      data-testid={`payroll-paid-btn-${s.employee_id}`}>
                      {s.paid ? <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Paid</> : 'Not paid'}
                    </Button>
                  ) : (
                    <Badge className={`rounded-sm text-[10px] uppercase ${s.paid
                      ? 'bg-green-500/15 text-green-400 border-green-500/30'
                      : 'bg-red-500/15 text-red-400 border-red-500/30'}`}
                      data-testid={`payroll-paid-badge-${s.employee_id}`}>
                      {s.paid ? 'Paid' : 'Not paid'}
                    </Badge>
                  )}
                  {s.payslip ? (
                    <>
                      {isMainAdmin && <DeptBadge deptTouch={s.payslip.dept_touch} targetId={s.payslip.id} />}
                      {s.payslip.approval_status === 'pending' ? (
                        <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30 rounded-sm text-[10px] uppercase" data-testid={`payslip-status-${s.employee_id}`}>
                          Pending approval
                        </Badge>
                      ) : s.payslip.approval_status === 'rejected' ? (
                        <Badge className="bg-red-500/15 text-red-400 border-red-500/30 rounded-sm text-[10px] uppercase" data-testid={`payslip-status-${s.employee_id}`}>
                          Rejected
                        </Badge>
                      ) : (
                        <Badge className={s.payslip.active
                          ? 'bg-green-500/15 text-green-400 border-green-500/30 rounded-sm text-[10px] uppercase'
                          : 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30 rounded-sm text-[10px] uppercase'}>
                          {s.payslip.active ? 'Visible' : 'Hidden'}
                        </Badge>
                      )}
                      {isMainAdmin && !['pending', 'rejected'].includes(s.payslip.approval_status) && (
                        <Switch checked={!!s.payslip.active} disabled={busyId === s.employee_id}
                          onCheckedChange={() => toggleActive(s)} data-testid={`payroll-toggle-${s.employee_id}`} />
                      )}
                      <Button size="sm" variant="ghost" onClick={() => openPayslip(s.payslip)}
                        className="h-7 px-2 text-[11px]" data-testid={`payroll-view-${s.employee_id}`}>
                        <Eye className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => pickFile(s)} disabled={busyId === s.employee_id}
                        className="h-7 px-2 text-[11px] rounded-sm" data-testid={`payroll-replace-${s.employee_id}`}>
                        <Upload className="w-3 h-3 mr-1" /> Replace
                      </Button>
                      <div
                        className={`hidden sm:flex items-center gap-1 h-7 px-2 rounded-sm border border-dashed text-[10px] uppercase tracking-wider transition-colors ${dragOverId === s.employee_id ? 'border-primary text-primary bg-primary/10' : 'border-border text-muted-foreground'}`}
                        data-testid={`payroll-dropzone-${s.employee_id}`}
                      >
                        <Download className="w-3 h-3" />
                        {dragOverId === s.employee_id ? 'Drop to upload' : 'Drag file here'}
                      </div>
                      {isMainAdmin && (
                        <Button size="sm" variant="ghost" onClick={() => removePayslip(s)}
                          className="h-7 px-2 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          data-testid={`payroll-delete-${s.employee_id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </>
                  ) : (
                    <>
                    <Button size="sm" variant="outline" onClick={() => pickFile(s)} disabled={busyId === s.employee_id}
                      className="h-7 rounded-sm text-[11px] uppercase border-primary/30 text-primary hover:bg-primary/10"
                      data-testid={`payroll-upload-${s.employee_id}`}>
                      {busyId === s.employee_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
                      Upload payslip
                    </Button>
                    <div
                      className={`hidden sm:flex items-center gap-1 h-7 px-2 rounded-sm border border-dashed text-[10px] uppercase tracking-wider transition-colors ${dragOverId === s.employee_id ? 'border-primary text-primary bg-primary/10' : 'border-border text-muted-foreground'}`}
                      data-testid={`payroll-dropzone-${s.employee_id}`}
                    >
                      <Download className="w-3 h-3" />
                      {dragOverId === s.employee_id ? 'Drop to upload' : 'Drag file here'}
                    </div>
                    </>
                  )}
                  </div>
                  {s.payslip?.approval_status === 'rejected' && s.payslip?.rejection_reason && (
                    <div className="mx-4 mb-3 px-3 py-2 rounded-sm border border-red-500/30 bg-red-500/5 text-xs" data-testid={`rejection-reason-${s.employee_id}`}>
                      <span className="text-red-400 font-semibold uppercase text-[10px] mr-2">Rejected by Primary Admin</span>
                      <span className="text-foreground/90">{s.payslip.rejection_reason}</span>
                      <span className="text-muted-foreground ml-2">— upload a corrected file to resubmit.</span>
                    </div>
                  )}
                  {isMainAdmin && expandedId === s.employee_id && (
                    <div className="mx-4 mb-3 px-3 py-2 rounded-sm border border-primary/25 bg-primary/5" data-testid={`payroll-bank-${s.employee_id}`}>
                      {bankLoading && !bankMap[s.employee_id] ? (
                        <Loader2 className="w-4 h-4 animate-spin text-primary" />
                      ) : (() => {
                        const b = bankMap[s.employee_id] || {};
                        const has = b.account_holder || b.sort_code || b.account_number;
                        return has ? (
                          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs">
                            <span><span className="text-muted-foreground uppercase text-[10px] mr-1">Holder</span> {b.account_holder || '—'}</span>
                            <span className="flex items-center gap-1">
                              <span className="text-muted-foreground uppercase text-[10px]">Sort code</span>
                              <span className="font-mono">{b.sort_code || '—'}</span>
                              {b.sort_code && <button onClick={() => copyVal('Sort code', b.sort_code)} className="text-primary hover:text-yellow-300" data-testid={`copy-sort-${s.employee_id}`}><Copy className="w-3 h-3" /></button>}
                            </span>
                            <span className="flex items-center gap-1">
                              <span className="text-muted-foreground uppercase text-[10px]">Account</span>
                              <span className="font-mono">{b.account_number || '—'}</span>
                              {b.account_number && <button onClick={() => copyVal('Account number', b.account_number)} className="text-primary hover:text-yellow-300" data-testid={`copy-acc-${s.employee_id}`}><Copy className="w-3 h-3" /></button>}
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-3 text-xs text-yellow-400">
                            ⚠ No bank details on file
                            <Button size="sm" variant="outline" onClick={() => navigate(`/admin/employees/${s.employee_id}`)}
                              className="h-6 px-2 text-[10px] rounded-sm" data-testid={`open-profile-${s.employee_id}`}>
                              <ExternalLink className="w-3 h-3 mr-1" /> Open profile
                            </Button>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
